import { Button, Icon, Menu, MenuPlacement } from '@shinetools/lumen-react'
import { Download } from 'lucide-react'
import { ReactElement, useCallback } from 'react'

import { useBalanceLabels } from '@features/balanceLedger'
import type { BalanceExportFormat } from '@features/balanceLedger/types/balanceExport'

// Single source of truth for the formats offered by the menu, so analytics' `exportOptionsAvailable` can't drift
// from the items actually rendered below.
export const BALANCE_EXPORT_FORMATS: BalanceExportFormat[] = ['pdf', 'xlsx']

// Static translation keys per format — avoids building `bt()` key paths with dynamic interpolation.
const EXPORT_FORMAT_LABEL_KEY: Record<BalanceExportFormat, string> = {
  pdf: 'export.pdf',
  xlsx: 'export.xlsx',
}

interface ExportMenuProps {
  isExporting: boolean
  onOpen?: () => void
  onSelectFormat: (format: BalanceExportFormat) => void
}

// Generic "Export" dropdown (pdf/xlsx) shared by the suppliers balance overview and the supplier statement.
export const ExportMenu = ({ isExporting, onOpen, onSelectFormat }: ExportMenuProps): ReactElement => {
  const { bt } = useBalanceLabels()

  const handleSelect = useCallback(
    (format: BalanceExportFormat) => () => {
      onSelectFormat(format)
    },
    [onSelectFormat],
  )

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        onOpen?.()
      }
    },
    [onOpen],
  )

  return (
    <Menu
      placement={MenuPlacement['bottom-end']}
      trigger={
        <Button
          variant="outlined"
          label={bt('export.label')}
          startIcon={<Icon icon={Download} />}
          loading={isExporting}
        />
      }
      aria-label={bt('export.label')}
      onOpenChange={onOpen ? handleOpenChange : undefined}
    >
      {BALANCE_EXPORT_FORMATS.map((format) => (
        <Menu.Item key={format} label={bt(EXPORT_FORMAT_LABEL_KEY[format])} onClick={handleSelect(format)} />
      ))}
    </Menu>
  )
}
